import { create } from 'zustand';

const useAutomationsStore = create((set, get) => ({
  // List page state
  workflows: [],
  isLoading: false,
  error: null,

  // Editor state
  currentWorkflow: null,
  nodes: [],
  edges: [],
  selectedNode: null,
  simulatingNodes: new Set(),
  isSimulating: false,

  // Version history
  versions: [],
  currentVersionId: null,

  // Company data for config selectors
  tokens: [],
  networks: [],
  setTokensAndNetworks: (tokens) => {
    const networks = tokens.reduce((acc, token) => {
      if (token.network && !acc.find((n) => n.id === token.network.id)) {
        acc.push(token.network);
      }
      return acc;
    }, []);
    set({ tokens, networks });
  },

  // --- List operations (called from pages with API hook) ---

  setWorkflows: (workflows) => set({ workflows }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  fetchWorkflows: async (api, companyId) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.listWorkflows(companyId);
      const workflows = Array.isArray(data) ? data : data?.workflows || [];
      set({ workflows, isLoading: false });
    } catch (err) {
      set({ error: err.message, isLoading: false });
    }
  },

  createWorkflow: async (api, companyId, name, description) => {
    try {
      const workflow = await api.createWorkflow(companyId, { name, description });
      set({ workflows: [workflow, ...get().workflows] });
      return workflow;
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteWorkflow: async (api, companyId, workflowId) => {
    try {
      await api.deleteWorkflow(companyId, workflowId);
      set({ workflows: get().workflows.filter((w) => w.id !== workflowId) });
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  toggleWorkflowStatus: async (api, companyId, workflowId) => {
    const workflow = get().workflows.find((w) => w.id === workflowId);
    if (!workflow) return;
    const newActive = !workflow.active;
    try {
      const updated = await api.updateWorkflow(companyId, workflowId, { active: newActive });
      set({
        workflows: get().workflows.map((w) => (w.id === workflowId ? { ...w, ...updated, active: newActive } : w)),
      });
      if (get().currentWorkflow?.id === workflowId) {
        set({ currentWorkflow: { ...get().currentWorkflow, ...updated, active: newActive } });
      }
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  // --- Editor operations ---

  loadWorkflow: async (api, companyId, workflowId, versionId) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.getWorkflow(companyId, workflowId, versionId);
      let nodes = [];
      let edges = [];

      if (data.file) {
        try {
          const fileContent = typeof data.file === 'string' ? JSON.parse(data.file) : data.file;
          nodes = fileContent.nodes || [];
          edges = fileContent.edges || [];
        } catch { /* empty on first load */ }
      }

      set({
        currentWorkflow: data,
        nodes,
        edges,
        selectedNode: null,
        currentVersionId: versionId || null,
        isLoading: false,
      });
      return data;
    } catch (err) {
      set({ error: err.message, isLoading: false });
      throw err;
    }
  },

  saveCurrentWorkflow: async (api, companyId) => {
    const { currentWorkflow, nodes, edges } = get();
    if (!currentWorkflow) return;
    try {
      await api.uploadWorkflowFile(companyId, currentWorkflow.id, { nodes, edges });

      // Re-fetch to get updated versions list from API
      const data = await api.getWorkflow(companyId, currentWorkflow.id);
      let latestNodes = [];
      let latestEdges = [];
      if (data.file) {
        try {
          const fileContent = typeof data.file === 'string' ? JSON.parse(data.file) : data.file;
          latestNodes = fileContent.nodes || [];
          latestEdges = fileContent.edges || [];
        } catch { /* empty */ }
      }

      set({
        currentWorkflow: data,
        nodes: latestNodes,
        edges: latestEdges,
        currentVersionId: null,
      });
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  updateWorkflowInfo: async (api, companyId, workflowId, data) => {
    try {
      const updated = await api.updateWorkflow(companyId, workflowId, data);
      set({
        workflows: get().workflows.map((w) => (w.id === workflowId ? { ...w, ...updated } : w)),
      });
      if (get().currentWorkflow?.id === workflowId) {
        set({ currentWorkflow: { ...get().currentWorkflow, ...updated } });
      }
      return updated;
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  runWorkflow: async (api, companyId, workflowId, inputData) => {
    try {
      return await api.runWorkflow(companyId, workflowId, inputData);
    } catch (err) {
      set({ error: err.message });
      throw err;
    }
  },

  // --- Local node/edge operations (no API calls) ---

  setNodes: (nodesOrUpdater) => {
    const newNodes = typeof nodesOrUpdater === 'function'
      ? nodesOrUpdater(get().nodes)
      : nodesOrUpdater;
    const selected = get().selectedNode;
    const updatedSelected = selected
      ? newNodes.find((n) => n.id === selected.id) || null
      : null;
    set({ nodes: newNodes, selectedNode: updatedSelected });
  },

  setEdges: (edgesOrUpdater) => {
    if (typeof edgesOrUpdater === 'function') {
      set({ edges: edgesOrUpdater(get().edges) });
    } else {
      set({ edges: edgesOrUpdater });
    }
  },

  addNode: (nodeData) => {
    const node = { id: crypto.randomUUID(), ...nodeData };
    set({ nodes: [...get().nodes, node] });
    return node;
  },

  updateNodeData: (nodeId, data) => {
    set({
      nodes: get().nodes.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n
      ),
    });
  },

  removeNode: (nodeId) => {
    set({
      nodes: get().nodes.filter((n) => n.id !== nodeId),
      edges: get().edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      selectedNode: get().selectedNode?.id === nodeId ? null : get().selectedNode,
    });
  },

  setSelectedNode: (node) => set({ selectedNode: node }),

  // --- Simulation ---

  simulateFromNode: async (startNodeId) => {
    const { edges } = get();
    if (get().isSimulating) return;
    set({ isSimulating: true, simulatingNodes: new Set() });

    const queue = [startNodeId];
    const visited = new Set();

    while (queue.length > 0) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);

      set({ simulatingNodes: new Set(visited) });
      await new Promise((r) => setTimeout(r, 600));

      const outgoing = edges.filter((e) => e.source === currentId);
      for (const edge of outgoing) {
        if (!visited.has(edge.target)) {
          queue.push(edge.target);
        }
      }
    }

    await new Promise((r) => setTimeout(r, 800));
    set({ isSimulating: false, simulatingNodes: new Set() });
  },

  clearEditor: () =>
    set({
      currentWorkflow: null,
      nodes: [],
      edges: [],
      selectedNode: null,
      simulatingNodes: new Set(),
      isSimulating: false,
      currentVersionId: null,
    }),
}));

export default useAutomationsStore;
